import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule } from '@angular/core';

import { AppComponent } from './app.component';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import { environment } from '../../../../globals/evasys/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { APP_BASE_HREF, DOCUMENT } from '@angular/common';
import { AuthUserService, ConfigService, SharedCoreConfiguration, SharedCoreModule } from '@evasys/shared/core';
import { SharedUtilModule, UserRightsPipe } from '@evasys/shared/util';
import { SharedFeatureModule } from '@evasys/shared/feature';
import { SharedUiModule } from '@evasys/shared/ui';
import { AppTranslationModule } from './app-translation.module';
import { EvasysUserModel } from '@evasys/globals/shared/models/general/evasys-user.model';
import { EvasysSharedFeatureModule } from '@evasys/evasys/shared/feature';
import { EvasysSharedCoreModule, SystemInfoService } from '@evasys/evasys/shared/core';
import { EvasysSharedUtilModule } from '@evasys/evasys/shared/util';
import { ApplicationInit, initColorSettings, initDefaultLanguage, initTitle } from './app.init';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { EvasysSharedUiModule } from '@evasys/evasys/shared/ui';
import { globalReducer } from '../../../../libs/shared/core/src/lib/reducers/global.reducer';
import { UserRightsEnum } from '@evasys/globals/shared/enums/business/user-rights.enum';
import { ExternalIdUserModel } from '@evasys/globals/evasys/models/business/external-id-user.model';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EvasysRevokeTokenTypeEnum } from '@evasys/globals/shared/models/general/evasys-revoke-token-type.enum';
import { StepFilterModule } from '../../../../libs/shared/ui/src/lib/components/dynamic-components/step-filter/step-filter.module';
import { TableModule } from '../../../../libs/shared/ui/src/lib/components/static-components/table/table.module';

const sharedCoreConfiguration = {
	api: {
		auth: 'oauth',
		user: 'users',
		translation: 'translate',
		config: 'configs',
		status: 'status',
		baseUrl: environment.apiHref,
		revokeTokenType: EvasysRevokeTokenTypeEnum.ACCESS_TOKEN,
	},
	auth: {
		clientId: environment.clientId,
		clientSecret: environment.clientSecret,
	},
	userMap: (apiData: any) => {
		const userRightPipe = new UserRightsPipe();
		const userRights = userRightPipe.transform(apiData.userRights);
		const adminRights = userRights?.find(
			(userRight) =>
				userRight === UserRightsEnum.ADMINISTRATOR || userRight === UserRightsEnum.SUBSECTION_ADMINISTRATOR
		);
		return {
			userRights,
			activeRight: userRights ? adminRights ?? userRights[0] : undefined,
			id: apiData.id,
			level: apiData.level,
			firstname: apiData.firstname,
			surname: apiData.surname,
			active: apiData.active,
			fullName: apiData.fullName ?? apiData._additionalData?.fullName,
			lang: apiData._additionalData?.languageSet.lang,
			flavor: apiData._additionalData?.languageSet.flavor,
			encryptedId: apiData.encryptedId,
			subunitLogo: apiData.subunitLogo,
			subunitName: apiData.subunitName,
			onlineTemplatesAccess: apiData.onlineTemplatesAccess,
		} as EvasysUserModel | ExternalIdUserModel;
	},
} as SharedCoreConfiguration;

@NgModule({
	declarations: [AppComponent],
	providers: [
		ApplicationInit,
		{ provide: APP_BASE_HREF, useValue: environment.baseHref },
		{
			provide: APP_INITIALIZER,
			useFactory: initTitle,
			deps: [ApplicationInit, DOCUMENT, SystemInfoService],
			multi: true,
		},
		{
			provide: APP_INITIALIZER,
			useFactory: initDefaultLanguage,
			deps: [ApplicationInit, HttpClient, TranslocoService, AuthUserService],
			multi: true,
		},
		{
			provide: APP_INITIALIZER,
			useFactory: initColorSettings,
			deps: [ApplicationInit, DOCUMENT, ConfigService],
			multi: true,
		},
	],
	imports: [
		//#region AppModules
		AppRoutingModule,
		AppTranslationModule,
		//#endregion AppModules
		//#region GeneralModules
		TranslocoModule,
		HttpClientModule,
		BrowserModule,
		BrowserAnimationsModule,
		ScrollingModule,
		//#endregion GeneralModules
		//#region ReduxModules
		StoreModule.forRoot({}, { metaReducers: [globalReducer] }),
		StoreDevtoolsModule.instrument({
			maxAge: 50,
			logOnly: environment.production,
		}),
		EffectsModule.forRoot(),
		//#endregion ReduxModules
		//#region SharedModules
		SharedUiModule.forRoot({
			translations: {
				wizard: {
					cancel: 'S_BUTTON_CANCEL',
					back: 'S_BUTTON_BACK',
					next: 'S_NEXT',
					submit: 'S_BUTTON_SAVE',
					errorMessages: {
						required: 'S_FIELD_REQUIRED',
						maxLength: 'S_FIELD_MAXLENGTH',
						minLength: 'S_FIELD_MINLENGTH',
						max: 'S_FIELD_MAX',
						min: 'S_FIELD_MIN',
						emailType: 'S_FIELD_EMAIL_TYPE',
						equal: 'S_FIELD_EQUAL',
						securePassword: 'S_PASSWORD_CHECK_GUIDELINE',
					},
				},
				uploadBox: {
					search: 'S_FILEINPUT_BROWSE',
					noFile: 'S_FILEINPUT_NO_FILE_SELECTED',
				},
				usermenu: {
					logoutButtonText: 'S_MENU_LOGOUT',
					changeRoleDropdownText: 'S_CHANGE_ROLE',
					switchUserText: 'S_MENU_CHANGEUSER',
					userMenuIconTitle: 'S_USER_MENU',
				},
				searchHelp: {
					searchMenuButtonText: 'S_MENU_SEARCH_SINGLE',
					helpMenuButtonText: 'S_HELP',
					searchSubmitButtonAltText: 'S_MENU_SEARCH',
					searchTextboxLabel: 'S_SEARCH_SYSTEM_LABEL',
					helpTextboxLabel: 'S_SEARCH_ONLINEHELP_LABEL',
				},
				systemUsage: {
					headlineTranslation: 'S_SYSTEMUSAGE_HEADLINE',
					systemTranslation: {
						evasys: 'S_SYSTEMUSAGE_MENU_USERS',
						evaexam: 'S_SYSTEMUSAGE_MENU_USERS_EXAM',
					},
					onlineUserTranslation: {
						evasys: 'S_SYSTEMUSAGE_MENU_ONLINE',
						evaexam: 'S_SYSTEMUSAGE_MENU_ONLINE_EXAM',
					},
				},
				pagination: {
					nextButtonText: 'S_BUTTON_NEXT',
					backButtonText: 'S_BUTTON_PREVIOUS',
				},
				sidebar: {
					permanentlyOpen: 'S_PERMANENTLY_OPEN',
				},
				popperMenu: {
					showAll: 'S_SHOW_ALL_ITEMS',
					noItemsFound: 'S_ITEMS_NOT_FOUND',
				},
				productsMenu: {
					switchTo: 'S_PRODUCT_MENU_SWITCH',
					products: 'S_PRODUCT_MENU_TOGGLE',
				},
				notificationCollection: {
					notificationIconTitle: '',
				},
				chipTypeahead: {
					selectAll: '',
					removeAll: '',
					removeAllTooltip: '',
				},
				legalNotice: {
					text: 'DATA_PRIVACY_TEXT',
					link: 'DATA_PRIVACY_LINK',
				},
				showMore: {
					showMore: '',
				},
				typeahead: {
					loading: '',
				},
			},
		}),
		StepFilterModule.forRoot({
			translation: {
				back: 'S_BUTTON_BACK',
				next: 'S_NEXT',
				clear: 'S_CLEAR',
			},
		}),
		TableModule.forRoot({
			translations: {
				tableHead: {
					sortAscending: 'S_ASCENDING',
					sortDescending: 'S_DESCENDING',
				},
			},
		}),
		SharedFeatureModule.forRoot({
			clientSecret: environment.clientSecret,
			isApiUrl: (requestUrl: string) => requestUrl.startsWith(environment.apiHref),
			catchAllHttpErrors: true,
		}),
		SharedCoreModule.forRoot(sharedCoreConfiguration),
		SharedUtilModule,
		//#endregion SharedModules
		//#region EvasysSharedModules
		EvasysSharedFeatureModule.forRoot({
			multiPageUrl: environment.oldWorldHref,
			prodMode: environment.production,
			baseHref: environment.baseHref,
		}),
		EvasysSharedCoreModule,
		EvasysSharedUtilModule,
		EvasysSharedUiModule,
		//#endregion EvasysSharedModules
	],
	bootstrap: [AppComponent],
})
export class AppModule {}
